import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Box } from "@mui/system";
import Toolbar from "@mui/material/Toolbar";
import AppSidebar from "../dashboard/AppSidebar";
import AppHeader from "./AppHeader";
import { makeStyles } from "@mui/styles";
import useInactivityTimeout from "../../pages/useInactivityTimeout";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
} from "@mui/material";
import { _init_without_token_api } from "../../DAL/Login/Login";
import { useAdminContext } from "../../hooks/UseAdminContext";
import { useSnackbar } from "notistack";
import AuthPrompt from "../dialogs/AuthPrompt";
import UnsavedChangesDialog from "../dialogs/UnsavedChangesDialog";

const useStyles = makeStyles(() => ({
  loading: {
    marginLeft: "50%",
    marginTop: "20%",
  },
}));

const allowedDomains = [
  "lhgeportal.com",
  "www.lhgevisa.com",
  "lhgevisa.com",
  "adminlhgeportal.com",
  "localhost",
];

const drawerWidth = 270;

const DashboardLayout = () => {
  const { showDialog, handleStayLoggedIn, handleLogout, countdown } =
    useInactivityTimeout(300 * 1000, 270 * 1000);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { pathname } = useLocation();
  const classes = useStyles();
  const navigate = useNavigate();
  const {
    setAdminSetting,
    setIsOpenAuthModal,
    isAuthenticated,
    setIsAuthenticated,
    showUnsavedDialog,
    setShowUnsavedDialog,
    actionToRunAfterLeavingUnsavedPage,
    setActionToRunAfterLeavingUnsavedPage,
    setHasUnsavedChanges,
  } = useAdminContext();

  const [isLoading, setIsLoading] = useState(true);
  const { enqueueSnackbar } = useSnackbar();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const get_setting_data = async () => {
    setIsLoading(true);
    const result = await _init_without_token_api();
    if (result.code == 200) {
      setIsLoading(false);
      setAdminSetting(result?.maintenance_settings);
      if (
        result?.maintenance_settings?.enable_developer_auth &&
        allowedDomains.includes(window.location.hostname)
      ) {
        // Only Show Auth Prompt if not already authenticated in this session
        if (sessionStorage.getItem("is_authenticated") === "true") {
          setIsAuthenticated(true);
        } else {
          setIsOpenAuthModal(true);
        }
      } else {
        setIsAuthenticated(true);
      }
      if (result?.maintenance_settings?.enable_maintenace_mode) {
        navigate("/under-maintenance", { replace: true });
      }
    } else {
      setIsLoading(false);
      enqueueSnackbar(result.message, { variant: "error" });
    }
  };

  useEffect(() => {
    get_setting_data();
  }, []);

  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  if (isLoading) {
    return <CircularProgress className={classes.loading} color="primary" />;
  }

  if (!localStorage.getItem("code-verified")) {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      {isAuthenticated ? (
        <>
          <Box sx={{ display: "flex" }}>
            <AppHeader
              drawerWidth={drawerWidth}
              handleDrawerToggle={handleDrawerToggle}
            />
            <AppSidebar
              mobileOpen={mobileOpen}
              drawerWidth={drawerWidth}
              handleDrawerClose={handleDrawerClose}
            />
            <Box
              component="main"
              sx={{
                flexGrow: 1,
                p: 2,
                width: `calc(100% - ${drawerWidth}px)`,
              }}
            >
              <Toolbar />
              <Outlet />
              <Dialog
                open={showDialog}
                aria-labelledby="inactivity-dialog-title"
                sx={{ "& .MuiDialog-paper": { borderRadius: 2, padding: 2 } }}
              >
                <DialogTitle id="inactivity-dialog-title">
                  Session Timeout Warning
                </DialogTitle>
                <DialogContent>
                  <Typography>
                    You will be logged out automatically in {countdown} seconds.
                    Do you want to stay logged in or log out?
                  </Typography>
                </DialogContent>
                <DialogActions>
                  <Button
                    onClick={handleStayLoggedIn}
                    variant="contained"
                    color="primary"
                    sx={{ mr: 1 }}
                  >
                    Stay Logged In
                  </Button>
                  <Button
                    onClick={handleLogout}
                    variant="contained"
                    color="error"
                  >
                    Logout
                  </Button>
                </DialogActions>
              </Dialog>

              <UnsavedChangesDialog
                open={showUnsavedDialog}
                onClose={() => setShowUnsavedDialog(false)}
                onLeave={() => {
                  setHasUnsavedChanges(false);
                  setShowUnsavedDialog(false);
                  if (actionToRunAfterLeavingUnsavedPage) {
                    if (typeof actionToRunAfterLeavingUnsavedPage === 'function') {
                      actionToRunAfterLeavingUnsavedPage();
                    }
                    setActionToRunAfterLeavingUnsavedPage(null);
                  }
                }}
              />
            </Box>
          </Box>
        </>
      ) : (
        <AuthPrompt />
      )}
    </>
  );
};

export default DashboardLayout;
